import { Capacitor } from '@capacitor/core';

const isNativeApp = () =>
  typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();

const isIosLike = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

const EXT_MIME = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  zip: 'application/zip',
  txt: 'text/plain'
};

export const mimeFromFileName = (fileName = '') => {
  const ext = String(fileName).split('.').pop()?.toLowerCase() || '';
  return EXT_MIME[ext] || 'application/octet-stream';
};

const resolveMime = (blob, fileName, contentType) => {
  const fromName = mimeFromFileName(fileName);
  const raw = (contentType || blob?.type || '').trim();
  if (!raw || raw === 'application/octet-stream' || raw === 'text/plain') {
    // text/plain + pdf/docx gibi uzantılar: app'te metin belgesi olarak kaydedilmesin
    if (fromName !== 'application/octet-stream' && fromName !== 'text/plain') return fromName;
  }
  if (raw && raw !== 'text/plain') return raw;
  return fromName;
};

const toFile = (blob, fileName, mime) => {
  try {
    return new File([blob], fileName || 'dosya', { type: mime });
  } catch {
    return new Blob([blob], { type: mime });
  }
};

/** Blob'u base64'e çevirir; Filesystem eklentisi yalnızca base64 kabul ediyor. */
const blobBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const okuyucu = new FileReader();
    okuyucu.onerror = () => reject(new Error('Dosya okunamadı.'));
    okuyucu.onload = () => {
      const sonuc = String(okuyucu.result || '');
      // "data:...;base64,XXXX" -> "XXXX"
      resolve(sonuc.slice(sonuc.indexOf(',') + 1));
    };
    okuyucu.readAsDataURL(blob);
  });

/** Dosya adındaki, dosya sisteminde sorun çıkaran karakterleri temizler. */
const temizAd = (ad) =>
  String(ad || 'belge').replace(/[\\/:*?"<>|]/g, '_').slice(0, 120) || 'belge';

/**
 * Uygulamada dosyayı gerçekten telefona yazar ve iOS paylaşım penceresini açar.
 * Başarılıysa sonuç döner, eklenti yoksa null döner.
 */
const nativeKaydet = async (blob, ad) => {
  let Filesystem;
  let Directory;
  let Share;
  try {
    ({ Filesystem, Directory } = await import('@capacitor/filesystem'));
    ({ Share } = await import('@capacitor/share'));
  } catch (e) {
    return null; // Eklenti kurulu değil (eski sürüm uygulama)
  }

  const dosyaAdi = temizAd(ad);
  const veri = await blobBase64(blob);

  // Cache klasörü: iOS temizlerse sorun olmaz, kullanıcı zaten paylaşım
  // penceresinden kalıcı bir yere kaydediyor.
  await Filesystem.writeFile({
    path: dosyaAdi,
    data: veri,
    directory: Directory.Cache,
    recursive: true,
  });

  const { uri } = await Filesystem.getUri({
    path: dosyaAdi,
    directory: Directory.Cache,
  });

  try {
    await Share.share({ title: dosyaAdi, url: uri, dialogTitle: 'Belgeyi kaydet' });
    return { method: 'native-share' };
  } catch (err) {
    // Kullanıcı paylaşımı kapattıysa bu bir hata değil.
    if (String(err?.message || '').toLowerCase().includes('cancel')) {
      return { method: 'share-cancelled' };
    }
    throw err;
  }
};

/**
 * Web + Capacitor indirme.
 * iOS'ta yalnızca canShare({files}) true ise paylaş; aksi halde blob URL ile aç/indir.
 * contentType verilirse MIME düzeltilir (metin belgesi hatasını önler).
 */
export const downloadBlob = async (blob, fileName = 'dosya', contentType = null) => {
  if (!blob) throw new Error('İndirilecek dosya bulunamadı.');

  const name = fileName || 'dosya';
  const mime = resolveMime(blob, name, contentType);
  const typedBlob = blob.type === mime ? blob : new Blob([blob], { type: mime });
  const file = toFile(typedBlob, name, mime);
  const nativeOrIos = isNativeApp() || isIosLike();

  // 0) UYGULAMA İÇİ GERÇEK KAYIT
  // Tarayıcıdaki navigator.share, WKWebView içinde dosya paylaşımını çoğu
  // zaman reddediyor; bu yüzden uygulamada "İndir" düğmesi eskiden gizliydi.
  // Burada dosyayı önce telefonun kendi geçici klasörüne yazıp sonra iOS'un
  // kendi paylaşım penceresini açıyoruz — böylece "Dosyalara Kaydet",
  // "Fotoğraflara Kaydet" ve diğer uygulamalara gönderme seçenekleri çıkıyor.
  if (isNativeApp()) {
    try {
      const kaydedilen = await nativeKaydet(typedBlob, name);
      if (kaydedilen) return kaydedilen;
    } catch (err) {
      // Kaydedilemezse aşağıdaki eski yollar denenir.
    }
  }

  // 1) Sadece gerçek dosya paylaşımı destekleniyorsa Share kullan
  if (
    nativeOrIos &&
    typeof navigator !== 'undefined' &&
    navigator.share &&
    navigator.canShare &&
    file instanceof File
  ) {
    try {
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: name });
        return { method: 'share' };
      }
    } catch (err) {
      if (err?.name === 'AbortError') return { method: 'share-cancelled' };
      // dosya paylaşımı başarısız → aşağıdaki indirme yoluna düş
    }
  }

  const url = window.URL.createObjectURL(typedBlob);

  // 2) Native/iOS: blob'u yeni sekmede aç (PDF/görsel görüntülenir, oradan kaydedilir)
  if (nativeOrIos) {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Ayrıca mümkünse pencere aç
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (_) {}
    setTimeout(() => {
      try { window.URL.revokeObjectURL(url); } catch (_) {}
    }, 180000);
    return { method: 'open' };
  }

  // 3) Masaüstü
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => {
    try { window.URL.revokeObjectURL(url); } catch (_) {}
  }, 5000);
  return { method: 'download' };
};

/**
 * Uygulamada "İndir" düğmesi gösterilebilir mi?
 *
 * NEDEN GEREKLİ: site güncellendiğinde telefondaki uygulama hâlâ eski
 * sürüm olabilir. Eski sürümde dosyayı cihaza yazacak eklenti yok;
 * düğme görünür ama iş görmezdi. Burada eklentinin gerçekten yüklü
 * olup olmadığına bakıyoruz. Tarayıcıda her zaman true.
 */
export const indirmeMumkunMu = () => {
  try {
    if (!isNativeApp()) return true;
    if (typeof Capacitor.isPluginAvailable !== 'function') return false;
    return Capacitor.isPluginAvailable('Filesystem') && Capacitor.isPluginAvailable('Share');
  } catch (e) {
    return false;
  }
};
